/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import _isEmpty from 'lodash/isEmpty';
import Box from '@material-ui/core/Box';
import ROUTES from 'constants/routes';
import { BRIGHT_GRAY } from 'constants/colors';
import { LOGIN } from 'constants/data-testid';
import Panel from 'components/Panel';
import Grid from '@material-ui/core/Grid';
import { FieldError } from 'components/Field';
import TextField, { FIELD_TYPES } from 'components/Mui/TextField';
import AppLogo, { appLogoVersions } from 'components/AppLogo';
import Button, { buttonColors, buttonSizes } from 'components/Button';
import Form from 'components/Form';
import { AUTH_ERRORS } from 'services/auth/constants';
import { useLocation } from 'react-router-dom';
import { LOGIN_ERRORS } from './constants';
import { exitContainer, signIn } from './actions';
import { getIsSubmitting, getLogInError } from './selectors';
import { FormWrap, LogoWrap, FormLink } from '../styles';
import './login.css';
import SandboxDialog from './SandboxDialog';
import { REACT_APP_SANDBOX_FORM_ID } from '../../../app/config';

const Login = ({ logInError, isSubmitting, onSignIn, goToResetPassword, onExit }) => {
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);

  const [account_id, setAccountId] = useState(urlParams.get('account_id'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleOnSubmit = () => {
    if (!_isEmpty(account_id) && !_isEmpty(username) && !_isEmpty(password)) {
      onSignIn({ account_id, username, password });
    }
  };

  useEffect(
    () => () => {
      onExit();
    },
    [onExit],
  );

  return (
    <Box className="login-container">
      <Panel data-testid={LOGIN.CONTENT_BOX}>
        <FormWrap>
          <LogoWrap>
            <AppLogo
              color={BRIGHT_GRAY}
              version={appLogoVersions.v2}
              link='https://commerceblitz.com'
            />
          </LogoWrap>
          <Form onSubmit={handleOnSubmit}>
            <Box
              display="flex"
              flexDirection="column"
              gridRowGap="1rem"
              width="100%"
            >
              <TextField
                variant="outlined"
                type={FIELD_TYPES.TEXT}
                label="Account ID"
                value={account_id}
                onChange={setAccountId}
                inputProps={{
                  'data-testid': LOGIN.FIELD_ACCOUNT_ID,
                }}
                autoComplete
                fullWidth
              />
              <TextField
                variant="outlined"
                type={FIELD_TYPES.TEXT}
                label="Username"
                value={username}
                onChange={setUsername}
                inputProps={{
                  'data-testid': LOGIN.FIELD_USERNAME,
                }}
                autoComplete
                fullWidth
              />
              <TextField
                variant="outlined"
                type={FIELD_TYPES.PASSWORD}
                label="Password"
                value={password}
                onChange={setPassword}
                size="large"
                inputProps={{
                  'data-testid': LOGIN.FIELD_PASSWORD,
                }}
                fullWidth
              />
              <Button
                type="submit"
                color={buttonColors.primary}
                size={buttonSizes.large}
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                data-testid={LOGIN.BTN_SIGN_IN}
              >
                <span>Sign In</span>
              </Button>
              {REACT_APP_SANDBOX_FORM_ID &&
                <SandboxDialog />
              }
            </Box>
            {logInError && (
              <FieldError data-testid={LOGIN.ERROR_MESSAGE}>
                <strong>{logInError}</strong>
              </FieldError>
            )}
          </Form>
          <Grid
            container
            justifyContent="flex-end"
            style={{ padding: '0.5rem 0' }}
          >
            <FormLink
              onClick={() => goToResetPassword()}
              data-testid={LOGIN.LINK_FORGOT_PASSWORD}
            >
              Forgot Password?
            </FormLink>
          </Grid>
        </FormWrap>
      </Panel>
      <Panel className="no-border-panel">
        <Box className="flex-box-wrap">
          <Box className="card-box">
            <Button
              color={buttonColors.light}
              size={buttonSizes.medium}
              fullWidth
              onClick={() => window.open('https://commerceblitz.com/freemium-sign-up/', '_blank')}
            >
              <span>Freemium Waitlist</span>
            </Button>
            <Box className="helper-text">Get early access to powerful tools free of charge</Box>
          </Box>

          <Box className="card-box">
            <Button
              color={buttonColors.light}
              size={buttonSizes.medium}
              fullWidth
              onClick={() => window.open('https://commerceblitz.com/schedule-a-demo/', '_blank')}
            >
              <span>Full Feature Demo</span>
            </Button>
            <Box className="helper-text">
              Already moving product?
              <br />
              Let us show you what CommerceBlitz can do
            </Box>
          </Box>
        </Box>
      </Panel>
    </Box>
  );
};

Login.propTypes = {
  onSignIn: PropTypes.func.isRequired,
  logInError: PropTypes.string,
  isSubmitting: PropTypes.bool.isRequired,
  goToResetPassword: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
};

Login.defaultProps = {
  logInError: null,
};

const mapStateToProps = state => {
  const error = getLogInError(state);
  const logInError =
    error === AUTH_ERRORS.NOT_AUTHORIZED ? LOGIN_ERRORS.INVALID_USERNAME_PASSWORD : error;

  return {
    isSubmitting: getIsSubmitting(state),
    logInError,
  };
};

const mapDispatchToProps = {
  onSignIn: signIn,
  goToResetPassword: () => push(ROUTES.LOGIN.RESET_PASSWORD),
  onExit: exitContainer,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
